/**
 * Client-side contamination detectors (v2 §22 — Goodhart / data pollution).
 *
 * These emit signals; they never judge. A detector firing lowers a session's validity
 * weight at ingest — it does not delete data and does not accuse anyone. "A parent
 * helped" and "the child is fast" are not distinguishable from telemetry, and the
 * product must not pretend otherwise.
 *
 * Thresholds here are deliberately conservative and are marked as assumptions rather
 * than findings: no measured child-response distribution exists yet for these tasks.
 * They are calibrated from the M1 pilot, not from this file.
 */
/** `[ASSUMPTION: A-28]` — floor below which a deliberate response is implausible. */
export const IMPLAUSIBLE_RESPONSE_MS = 250;
/** `[ASSUMPTION: A-28]` — a gap this long inside a trial suggests interruption. */
export const LONG_GAP_MS = 30_000;
/**
 * A response faster than a child can perceive-and-act. Note this uses response time
 * ONLY as a contamination signal, never as a processing-speed measure — PC-1 excludes
 * reaction-time constructs, and device timing variance is exactly why.
 */
export function detectImplausibleSpeed(responseMs, itemCount) {
    // Scale with sequence length: reproducing 5 items cannot be faster than 2.
    const floor = IMPLAUSIBLE_RESPONSE_MS * Math.max(1, itemCount * 0.5);
    if (responseMs < floor) {
        return {
            anomaly: "implausible_speed",
            detail: `responseMs=${Math.round(responseMs)} below floor=${Math.round(floor)} for ${itemCount} items`,
        };
    }
    return null;
}
/**
 * More simultaneous touch points than one small hand produces on a tap task.
 * Two hands on a tablet is normal play; three-plus concurrent points during a scored
 * trial more often means a second person is involved.
 */
export function detectMultiTouch(concurrentPoints) {
    if (concurrentPoints >= 3) {
        return {
            anomaly: "multi_touch",
            detail: `concurrentPoints=${concurrentPoints}`,
        };
    }
    return null;
}
/**
 * A burst of taps landing outside any target. Distinguishes frustration or a child
 * exploring the screen from purposeful responding — relevant to v2 §19, since
 * frustration must not be scored as inability.
 */
export function detectOffTargetBurst(offTargetTapsInWindow, windowMs) {
    if (offTargetTapsInWindow >= 5 && windowMs <= 2000) {
        return {
            anomaly: "off_target_burst",
            detail: `${offTargetTapsInWindow} off-target taps in ${windowMs}ms`,
        };
    }
    return null;
}
/** Tracks concurrent pointer count so `detectMultiTouch` has something to read. */
export class TouchTracker {
    #active = new Set();
    #peak = 0;
    down(pointerId) {
        this.#active.add(pointerId);
        this.#peak = Math.max(this.#peak, this.#active.size);
    }
    up(pointerId) {
        this.#active.delete(pointerId);
    }
    /** Peak concurrency since the last reset. */
    get peak() {
        return this.#peak;
    }
    resetPeak() {
        this.#peak = this.#active.size;
    }
}
//# sourceMappingURL=detectors.js.map